import { apiFetch, apiDownload } from "./client";
import type {
  ApiResponse,
  Empresa,
  Factura,
  FacturaDetail,
  PucCuenta,
  UpdateFacturaRequest,
  User,
} from "@/types/api";

export interface EmpresasResponse extends ApiResponse<Empresa[]> {
  empresa_activa?: number | null;
}

export const fetchUser = () => apiFetch<User>("/api/v1/auth/me");

export const fetchEmpresas = () =>
  apiFetch<EmpresasResponse>("/api/v1/usuario/empresas");

export const setEmpresaActiva = (empresa_id: number) =>
  apiFetch<ApiResponse<null>>("/api/v1/usuario/empresa_activa", {
    method: "POST",
    body: JSON.stringify({ empresa_id }),
  });

export interface ListFacturasParams {
  limit?: number;
  offset?: number;
  last_id?: number;
  empresa_id?: number;
}

export const listFacturas = (params?: ListFacturasParams) =>
  apiFetch<ApiResponse<Factura[]>>("/api/v1/facturas", {
    params: { ...params, _t: Date.now() },
    cache: "no-store",
    headers: {
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
  }).then((res) => res.data ?? []);

export const getFactura = (id: number, empresa_id?: number) =>
  apiFetch<ApiResponse<FacturaDetail>>(`/api/v1/facturas/${id}`, {
    params: { _t: Date.now(), empresa_id },
    cache: "no-store",
  }).then((res) => res.data as FacturaDetail);

export const updateFactura = (id: number, body: UpdateFacturaRequest) =>
  apiFetch<ApiResponse<null>>(`/api/v1/facturas/${id}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });

export const deleteFactura = (id: number) =>
  apiFetch<ApiResponse<null>>(`/api/v1/facturas/${id}`, { method: "DELETE" });

export const retryFactura = (id: number, empresa_id?: number) =>
  apiFetch<ApiResponse<null>>(`/api/v1/facturas/${id}/reintentar`, {
    method: "POST",
    params: { empresa_id },
  });

export const deleteAllFacturas = (empresa_id?: number) =>
  apiFetch<ApiResponse<null>>("/api/v1/facturas/todas", {
    method: "DELETE",
    params: { empresa_id },
  });

export const exportSiigo = (ids?: number[], empresa_id?: number) =>
  apiDownload(
    "/api/v1/facturas/exportar/siigo",
    {
      ids: ids && ids.length ? ids.join(",") : undefined,
      empresa_id,
    },
    "facturas_siigo.xlsx",
  );

export const exportExcel = (ids?: number[], empresa_id?: number) =>
  apiDownload(
    "/api/v1/facturas/exportar/excel",
    {
      ids: ids && ids.length ? ids.join(",") : undefined,
      empresa_id,
    },
    "facturas.xlsx",
  );

export const searchPuc = (q: string, empresa_id?: number, limit = 10) =>
  apiFetch<ApiResponse<PucCuenta[]>>("/api/v1/puc/buscar", {
    params: { q, limit, empresa_id },
  }).then((res) => res.data ?? []);

export const getPucByCodigo = (codigo: string, empresa_id?: number) =>
  apiFetch<ApiResponse<PucCuenta>>(`/api/v1/puc/${encodeURIComponent(codigo)}`, {
    params: { empresa_id },
  }).then((res) => res.data);

export const clearSessionCache = () =>
  apiFetch<ApiResponse<null>>("/api/v1/cache/clear-session", {
    method: "POST",
  });

export interface UploadParams {
  files: File[];
  client_id: string;
  empresa_id?: number;
}

export const uploadFiles = async ({
  files,
  client_id,
  empresa_id,
}: UploadParams) => {
  const formData = new FormData();
  for (const file of files) formData.append("files", file);

  return apiFetch<ApiResponse<null>>("/api/v1/upload", {
    method: "POST",
    body: formData,
    params: { client_id, empresa_id },
  });
};
